import React, { useContext } from 'react';

// molecules
import ErrorHandler from '../../../_molecules/ErrorHandler';
import TrackPageView from '@tracking/TrackPageView';
import LoadingArea from '@molecules/LoadingArea';

// functionality
import useViewer from '@hooks/useViewer';
import NewPassword from '../../../_molecules/NewPassword';
import { updateProfilePassword } from '@general/api/memberAPI';
import {DividerLightGreyStyled, H3, SectionWrapperInner, SectionWrapperOuter} from '@styled';
import { PasswordChangeContext } from '@context/PasswordChangeProvider';
import { LoadingContext } from '@context/LoadingProvider';
import LoggedInResetPassword from './LoggedInResetPassword';
import SectionHeader from "../../../_molecules/SectionHeader";
import SectionBreadcrumbs from "../../../_molecules/SectionBreadcrumbs";
import SectionBreadcrumb from "../../../_molecules/SectionBreadcrumb";
import EditProfileNavLink from "@molecules/EditProfileNavLink";
import classNames from "classnames";

const UpdatePasswordPage = () => {
    const { setIsLoading } = useContext(LoadingContext);
    const { setCurrentPasswordError, setNewPasswordError, setConfirmPasswordError } = useContext(PasswordChangeContext);
    const { viewer } = useViewer();

    const isOfficial = viewer && viewer.official;

    const prevNavLink = isOfficial ? '/my-icsc/account/edit-company' : '/my-icsc/account/edit-profile';
    const prevNavLabel = isOfficial ? 'Edit Company Profile' : 'Edit Profile';
    const nextNavLink = '/my-icsc/account/edit-billing';
    const nextNavLabel = 'Billing & Payments';
    const navClasses = classNames({
        'd-flex': true,
        'justify-content-between': prevNavLink && nextNavLink,
        'justify-content-end': !prevNavLink && nextNavLink,
    });

    if (!viewer) {
        return <LoadingArea />;
    }

    const updatePassword = async ({ currentPassword, newPassword, confirmPassword }) => {
        setIsLoading(true);

        try {
            await updateProfilePassword(viewer.recordId, currentPassword, newPassword, confirmPassword);
            window.location = '/login';
        } catch (e) {
            const errors = e.response.data?.errors;

            if (errors.includes('current_password_incorrect')) {
                setCurrentPasswordError('Current password is incorrect.');
            } else if (errors.includes('new_password_mismatch')) {
                setConfirmPasswordError('Password does not match.');
            } else if (errors.includes('breached_password')) {
                setNewPasswordError('This password was found in a list of breached passwords and cannot be used.');
            } else {
                setNewPasswordError(errors[0] ?? 'Please choose a stronger password.');
            }

            setIsLoading(false);
        }
    };

    if (window.canChangePassword === false) {
        return <LoggedInResetPassword email={viewer.email} />;
    }

    return (
        <ErrorHandler>
            <TrackPageView>
                <SectionWrapperOuter>
                    <SectionWrapperInner className="fade-in-fast">
                        <SectionHeader title="My Account"/>
                        <SectionBreadcrumbs className="mt-2">
                            <SectionBreadcrumb route="/my-icsc/account">My Account</SectionBreadcrumb>
                            <SectionBreadcrumb isActive>Update Password</SectionBreadcrumb>
                        </SectionBreadcrumbs>
                        <H3 className="mt-3 mb-4">Update Password</H3>
                        <NewPassword withCurrent email={viewer.email} onUpdate={updatePassword}/>
                    </SectionWrapperInner>

                    <DividerLightGreyStyled className="mt-5 mb-4" />
                    <div className={navClasses}>
                        {prevNavLink && (
                            <EditProfileNavLink type="prev" to={prevNavLink}>
                                {prevNavLabel}
                            </EditProfileNavLink>
                        )}
                        {nextNavLink && (
                            <EditProfileNavLink type="next" to={nextNavLink}>
                                {nextNavLabel}
                            </EditProfileNavLink>
                        )}
                    </div>
                </SectionWrapperOuter>
            </TrackPageView>
        </ErrorHandler>
    );
};

export default UpdatePasswordPage;
